import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import { NavLink } from "react-router";
import { useTranslation } from "react-i18next";
import { RoutesPaths } from "../Router/constant";

const momentCardIcons = [
  "/images/economic-diversification-icon.png",
  "/images/smart-capital-icon.png",
  "/images/disciplined-execution-icon.png",
];

export default function Home() {
  const { t } = useTranslation();
  const cards = t("pages.home.moment.cards", { returnObjects: true });
  const data = t("pages.home.operating.cards", { returnObjects: true });
  const solutionContent = t("pages.home.solutions.items", { returnObjects: true });
  const esgItems = t("pages.home.esg.items", { returnObjects: true });

  const solutions = [
    {
      ...solutionContent[0],
      img: "/images/solution-block-image.jpg",
      icon: "/images/icon-bag.png",
      link: RoutesPaths.PRIVATE_SECTOR,
    },
    {
      ...solutionContent[1],
      img: "/images/solution-block-image.jpg",
      icon: "/images/icon-house.png",
      link: RoutesPaths.PUBLIC_SECTOR,
    },
  ];

  const items = [
    { ...esgItems[0], icon: "/images/message-chat-circle.png" },
    { ...esgItems[1], icon: "/images/zap.png" },
    { ...esgItems[2], icon: "/images/chart-breakout-square.png" },
    { ...esgItems[3], icon: "/images/chart-breakout-square.png" },
  ];

  const [index, setIndex] = useState(0);
  const [slideStep, setSlideStep] = useState(370);
  const [loopPadding, setLoopPadding] = useState(5);
  const [sliderTransition, setSliderTransition] = useState(true);
  const sliderRef = useRef(null);
  const sliderMoving = useRef(false);
  const loopedCards = Array.from(
    { length: data.length + loopPadding * 2 },
    (_, position) => data[((position - loopPadding) % data.length + data.length) % data.length]
  );

  const next = () => {
    if (sliderMoving.current) return;
    sliderMoving.current = true;
    setIndex((current) => current + 1);
  };

  const prev = () => {
    if (sliderMoving.current) return;
    sliderMoving.current = true;
    setIndex((current) => current - 1);
  };

  useLayoutEffect(() => {
    const slider = sliderRef.current;
    const measure = () => {
      const track = slider.querySelector(".slider-track");
      const card = track.querySelector(".slide-card");
      const step = card.getBoundingClientRect().width + parseFloat(getComputedStyle(track).gap);
      setSlideStep(step);
      setLoopPadding(Math.ceil(slider.clientWidth / step) + 1);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(slider);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (sliderTransition) return;
    let secondFrame;
    const firstFrame = requestAnimationFrame(() => {
      secondFrame = requestAnimationFrame(() => {
        setSliderTransition(true);
        sliderMoving.current = false;
      });
    });
    return () => {
      cancelAnimationFrame(firstFrame);
      cancelAnimationFrame(secondFrame);
    };
  }, [sliderTransition]);

  const finishSlide = (event) => {
    if (event.target !== event.currentTarget || event.propertyName !== "transform") return;
    if (index < 0 || index >= data.length) {
      setSliderTransition(false);
      setIndex((current) => (current + data.length) % data.length);
    } else {
      sliderMoving.current = false;
    }
  };

  useEffect(() => {
    const elements = document.querySelectorAll(".reveal-on-scroll");

    const observer = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const delay = entry.target.dataset.delay;
            if (delay) {
              entry.target.style.transitionDelay = `${delay}ms`;
            }

            entry.target.classList.add("show");
            obs.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.2,
        rootMargin: "0px 0px -50px 0px",
      }
    );

    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <>
      <div className="section section-banner">
        <video className="bgVideo" autoPlay muted loop playsInline>
          <source src="/images/banner-video.mp4" type="video/mp4" />
        </video>
        <div className="main-container container-section-banner">
          <div className="row justify-content-center">
            <div className="col-lg-8 col-md-8 text-center">
              <h1
                className="m-0 f-w-600 f-s-52 l-h-64 c-s home-banner-title reveal-on-scroll fade-up"
                data-delay="100"
              >
                {t("pages.home.hero.title")}
              </h1>
              <NavLink
                className="contact-btn d-flex align-items-center f-w-600 f-s-14 l-h-100 mx-auto reveal-on-scroll fade-up"
                to={RoutesPaths.CONTACT}
                data-delay="200"
              >
                <span className="btn-text">{t("common.cta.contact")}</span>
                <span className="arrow-circle">
                  <img src="/images/right-angle-arrow.png" alt="icon" />
                </span>
              </NavLink>
            </div>
          </div>
        </div>
      </div>

      <div className="section-body-bellow">
        <div className="section-common">
          <div className="section section-momentent">
            <div className="main-container">
              <div className="text-center title-and-desc">
                <h2
                  className="section-title f-w-600 f-s-48 l-h-60 m-0 c-p reveal-on-scroll fade-up"
                  data-delay="300"
                >
                  {t("pages.home.moment.title")}
                </h2>
                <p
                  className="section-subtitle c-p f-s-20 l-h-30 m-0 reveal-on-scroll fade-up"
                  data-delay="400"
                >
                  {t("pages.home.moment.subtitle")}
                </p>
              </div>

              <div className="row g-4">
                {cards.map((item, itemIndex) => (
                  <div className="col-md-4" key={item.title}>
                    <div
                      className="info-card reveal-on-scroll fade-left"
                      data-delay={`${(itemIndex + 1) * 200}`}
                    >
                      <div className="icon-box">
                        <img src={momentCardIcons[itemIndex]} alt="" className="moment-card-icon" width="40" height="40" />
                      </div>

                      <h5 className="m-0 f-w-600 f-s-24 l-h-32 c-s-w">
                        {item.title}
                      </h5>

                      <p className="m-0 f-s-16 l-h-20 c-s-w">{item.text}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="section section-momentent">
            <div className="main-container">
              <div className="text-center title-and-desc">
                <h2
                  className="section-title f-w-600 f-s-48 l-h-60 m-0 c-p reveal-on-scroll fade-left"
                  data-delay="300"
                >
                  {t("pages.home.positioning.title")}
                </h2>
                <p
                  className="section-subtitle c-p f-s-20 l-h-30 m-0 reveal-on-scroll fade-right"
                  data-delay="400"
                >
                  {t("pages.home.positioning.subtitle")}
                </p>
              </div>

              <div className="row align-items-stretch g-4 positioning-cards-row">
                <div className="col-md-4 text-center col-strategy-image d-block d-md-none">
                  <div className="strategy-image reveal-on-scroll fade-left" data-delay="500">
                    <img src="/images/chess-image.jpg" alt="strategy" className="img-fluid" />
                  </div>
                </div>

                <div className="col-md-4 col-strategy-card">
                  <div className="strategy-card reveal-on-scroll fade-up" data-delay="500">
                    <span className="strategy-card__icon-circle">
                      <img src="/images/positioning-strategy-icon.png" alt="" className="strategy-card__icon" width="48" height="48" />
                    </span>
                    <h5 className="m-0 f-w-600 f-s-24 l-h-32 c-p">
                      {t("pages.home.positioning.strategyTitle")}
                    </h5>

                    <p className="m-0 f-s-16 l-h-24 c-p">
                      {t("pages.home.positioning.strategyText")}
                    </p>

                    <ul className="strategy-list f-s-16 l-h-20 c-p">
                      {t("pages.home.positioning.strategyPoints", {
                        returnObjects: true,
                      }).map((point) => (
                        <li key={point}>{point}</li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="col-md-4 text-center col-strategy-image d-none d-md-block">
                  <div className="strategy-image reveal-on-scroll fade-up" data-delay="500">
                    <img src="/images/chess-image.jpg" alt="strategy" className="img-fluid" />
                  </div>
                </div>

                <div className="col-md-4 col-strategy-card">
                  <div className="strategy-card reveal-on-scroll fade-right" data-delay="500">
                    <span className="strategy-card__icon-circle">
                      <img src="/images/positioning-problem-solving-icon.png" alt="" className="strategy-card__icon" width="48" height="48" />
                    </span>
                    <h5 className="m-0 f-w-600 f-s-24 l-h-32 c-p">
                      {t("pages.home.positioning.problemTitle")}
                    </h5>

                    <p className="m-0 f-s-16 l-h-24 c-p">
                      {t("pages.home.positioning.problemText")}
                    </p>

                    <ul className="strategy-list f-s-16 l-h-20 c-p">
                      {t("pages.home.positioning.problemPoints", {
                        returnObjects: true,
                      }).map((point) => (
                        <li key={point}>{point}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="section section-who-we-are">
            <div className="main-container">
              <div className="row g-0 align-items-stretch justify-content-end">
                <div className="col-md-6 d-flex">
                  <div className="who-content">
                    <h2
                      className="m-0 f-w-600 f-s-48 l-h-60 c-s-w reveal-on-scroll fade-right"
                      data-delay="300"
                    >
                      {t("pages.home.who.title")}
                    </h2>

                    <p
                      className="m-0 f-s-20 l-h-30 c-s-w who-subtext reveal-on-scroll fade-right"
                      data-delay="400"
                    >
                      {t("pages.home.who.subtitle")}
                    </p>

                    <div className="who-card reveal-on-scroll fade-right" data-delay="500">
                      <div className="card-top">
                        <div className="icon-box">
                          <img src="/images/who-mission-icon.png" alt="" className="who-card__icon" width="40" height="40" />
                        </div>

                        <div className="arrow-box">
                          <img src="/images/arrow.png" alt="arrow" />
                          <img src="/images/arrow-dark.png" alt="arrow" />
                        </div>
                      </div>

                      <h6 className="m-0 f-w-600 f-s-24 l-h-32 c-s-w">
                        {t("pages.home.who.missionTitle")}
                      </h6>

                      <p className="m-0 f-s-16 l-h-24 c-s-w">
                        {t("pages.home.who.missionText")}
                      </p>
                    </div>

                    <div className="who-card mt-3 reveal-on-scroll fade-left" data-delay="600">
                      <div className="card-top">
                        <div className="icon-box">
                          <img src="/images/who-vision-icon.png" alt="" className="who-card__icon" width="40" height="40" />
                        </div>

                        <div className="arrow-box">
                          <img src="/images/arrow.png" alt="arrow" />
                          <img src="/images/arrow-dark.png" alt="arrow" />
                        </div>
                      </div>

                      <h6 className="m-0 f-w-600 f-s-24 l-h-32 c-s-w">
                        {t("pages.home.who.visionTitle")}
                      </h6>

                      <p className="m-0 f-s-16 l-h-24 c-s-w">
                        {t("pages.home.who.visionText")}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="section section-slider">
          <div className="main-container d-flex justify-content-between align-items-end mb-5">
            <div>
              <h2
                className="m-0 f-w-600 f-s-48 l-h-60 c-p slider-main-title reveal-on-scroll fade-left"
                data-delay="100"
              >
                {t("pages.home.operating.title")}
              </h2>
              <p className="m-0 f-s-20 l-h-30 c-p reveal-on-scroll fade-left" data-delay="200">
                {t("pages.home.operating.subtitle")}
              </p>
            </div>

            <div className="slider-controls d-none d-lg-flex">
              <button
                onClick={prev}
                className="nav-btn"
                aria-label={t("common.actions.previous")}
              >
                &larr;
              </button>
              <button
                onClick={next}
                className="nav-btn"
                aria-label={t("common.actions.next")}
              >
                &rarr;
              </button>
            </div>
          </div>

          <div className="slider-wrapper" ref={sliderRef}>
            <div
              className="slider-track"
              onTransitionEnd={finishSlide}
              style={{
                transform: `translateX(-${(loopPadding + index) * slideStep}px)`,
                transition: sliderTransition ? undefined : "none",
              }}
            >
              {loopedCards.map((item, position) => (
                <div className="slide-card" key={`${item.id}-${position}`}>
                  <div className="slide-number f-w-700">{item.id}</div>

                  <h6 className="m-0 text-center f-w-600 f-s-20 l-h-30 c-p">
                    {item.title}
                  </h6>

                  <ul>
                    {item.points.map((point) => (
                      <li className="f-s-16 l-h-24 c-p" key={point}>
                        {point}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          <div className="main-container d-flex d-lg-none justify-content-center align-items-end mt-3">
            <div className="slider-controls">
              <button
                onClick={prev}
                className="nav-btn"
                aria-label={t("common.actions.previous")}
              >
                &larr;
              </button>
              <button
                onClick={next}
                className="nav-btn"
                aria-label={t("common.actions.next")}
              >
                &rarr;
              </button>
            </div>
          </div>
        </div>

        <div className="section section-momentent">
          <div className="main-container">
            <div className="text-center title-and-desc">
              <h2
                className="section-title f-w-600 f-s-48 l-h-60 m-0 c-p reveal-on-scroll fade-right"
                data-delay="300"
              >
                {t("pages.home.solutions.title")}
              </h2>
              <p
                className="section-subtitle c-p f-s-20 l-h-30 m-0 reveal-on-scroll fade-left"
                data-delay="400"
              >
                {t("pages.home.solutions.subtitle")}
              </p>
            </div>

            <div className="main-container container-inside">
              <div className="row gy-4 gx-0 gy-lg-5 gx-lg-5">
                {solutions.map((item, itemIndex) => (
                  <div className="col-md-6" key={item.title}>
                    <div
                      className="solution-card reveal-on-scroll fade-up"
                      data-delay={`${(itemIndex + 1) * 500}`}
                    >
                      <div className="solution-img">
                        <img src={item.img} alt={item.title} />
                      </div>

                      <div className="solution-content">
                        <div className="title-row">
                          <div className="icon-box-solutions">
                            <img src={item.icon} alt={item.title} />
                          </div>
                          <h5 className="m-0 f-w-600 f-s-36 l-h-40 c-p">{item.title}</h5>
                        </div>

                        <p className="m-0 f-s-20 l-h-30 c-p">{item.desc}</p>

                        <NavLink className="learn-more f-w-600 f-s-16 l-h-24" to={item.link}>
                          {t("common.cta.learnMore")}
                          <img src="/images/arrow-right_10.png" alt="icon arrow" />
                        </NavLink>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="section">
          <div className="main-container mb-5">
            <div className="row">
              <div className="col-lg-7 col-md-7">
                <h2
                  className="m-0 f-w-600 f-s-48 l-h-60 c-p esg-title reveal-on-scroll fade-left"
                  data-delay="300"
                >
                  {t("pages.home.esg.title")}
                </h2>
                <p
                  className="m-0 f-s-20 l-h-30 c-p reveal-on-scroll fade-left"
                  data-delay="400"
                >
                  {t("pages.home.esg.subtitle")}
                </p>
              </div>

              <div className="col-lg-5 col-md-5 d-lg-flex justify-content-end d-none">
                <NavLink
                  className="contact-btn d-flex align-items-center f-w-600 f-s-14 l-h-100"
                  to={RoutesPaths.CONTACT}
                >
                  <span className="btn-text">{t("common.cta.contact")}</span>

                  <span className="arrow-circle">
                    <img src="/images/right-angle-arrow.png" alt="icon" />
                  </span>
                </NavLink>
              </div>
            </div>
          </div>

          <div className="main-container">
            <div className="row align-items-center g-4">
              <div className="col-md-6 col-items reveal-on-scroll fade-lrft" data-delay="500">
                {items.map((item) => (
                  <div className="esg-item d-flex align-items-center" key={item.title}>
                    <div className="icon-box-esg">
                      <img src={item.icon} alt={item.title} />
                    </div>

                    <div>
                      <h6 className="m-0 f-w-600 f-s-20 l-h-30 c-p">{item.title}</h6>
                      <p className="m-0 f-s-16 l-h-16 c-p">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="col-md-6">
                <div className="esg-image reveal-on-scroll fade-left" data-delay="600">
                  <img className="esg-image-banner" src="/images/esg-banner.jpg" alt="esg" />

                  <NavLink
                    className="contact-btn d-flex d-md-none align-items-center f-w-600 f-s-14 l-h-100"
                    to={RoutesPaths.CONTACT}
                  >
                    <span className="btn-text">{t("common.cta.contact")}</span>

                    <span className="arrow-circle">
                      <img src="/images/right-angle-arrow.png" alt="icon" />
                    </span>
                  </NavLink>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="section section-stratagic">
          <div className="main-container">
            <div className="row justify-content-center">
              <div className="col-lg-7 col-md-7 text-center px-3">
                <h1
                  className="m-0 f-w-600 f-s-48 l-h-60 c-s stratagic-banner-title reveal-on-scroll fade-up"
                  data-delay="300"
                >
                  {t("common.siteCta.title")}
                </h1>
                <p
                  className="m-0 f-s-20 l-h-30 c-s stratagic-desc reveal-on-scroll fade-up"
                  data-delay="400"
                >
                  {t("common.siteCta.text")}
                </p>

                <NavLink
                  className="contact-btn d-flex align-items-center f-w-600 f-s-14 l-h-100 mx-auto"
                  to={RoutesPaths.CONTACT}
                >
                  <span className="btn-text">{t("common.cta.contact")}</span>

                  <span className="arrow-circle">
                    <img src="/images/right-angle-arrow.png" alt="icon" />
                  </span>
                </NavLink>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
